<template>
  <!--
    Monthly usage-allowance meter. Percentage-only by design: end users
    never see dollars or token counts (those live in Admin → Atlas Usage).
    Renders nothing when the client has no budget configured (allowance
    null). Neutral below 80%, amber from 80%, red at 100% (blocked).
  -->
  <div v-if="meter" class="alw-meter" :title="title">
    <div class="alw-meter-bar">
      <div
        class="alw-meter-fill"
        :class="meter.level"
        :style="{ width: meter.pct + '%' }"
      />
    </div>
    <span class="alw-meter-label">Allowance {{ meter.pct }}%</span>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'AllowanceMeter',
  props: {
    /** ChatResponse.usage_allowance shape (or null): { pct_used, warn, blocked } */
    allowance: { type: Object, default: null },
  },
  setup(props) {
    const meter = computed(() => {
      const a = props.allowance;
      if (!a || typeof a.pct_used !== 'number') return null;
      return {
        pct: Math.min(100, Math.max(0, Math.round(a.pct_used))),
        level: a.blocked ? 'is-danger' : a.warn ? 'is-warn' : 'is-ok',
      };
    });

    const title = computed(() => {
      const a = props.allowance;
      if (!a) return '';
      // scope names the binding budget: 'user' = this user's own cap,
      // anything else = the whole client's.
      const whose = a.scope === 'user' ? 'your personal' : "this month's";
      if (a.blocked) {
        return a.scope === 'user'
          ? 'Your personal monthly Atlas usage allowance is used up — Atlas will be available to you again at the start of next month.'
          : 'Monthly Atlas usage allowance reached — Atlas will be available again at the start of next month.';
      }
      if (a.warn) {
        return `You're approaching ${whose} Atlas usage allowance (${a.pct_used}% used).`;
      }
      return `${a.pct_used}% of ${whose} Atlas usage allowance used.`;
    });

    return { meter, title };
  },
});
</script>

<style scoped>
.alw-meter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 9999px;
  background: rgb(243 244 246);
  cursor: default;
  user-select: none;
}
.alw-meter-bar {
  width: 64px;
  height: 6px;
  border-radius: 9999px;
  background: rgb(229 231 235);
  overflow: hidden;
}
.alw-meter-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.4s ease, background-color 0.4s ease;
}
.alw-meter-fill.is-ok {
  background: rgb(79 70 229);
}
.alw-meter-fill.is-warn {
  background: rgb(217 119 6);
}
.alw-meter-fill.is-danger {
  background: rgb(220 38 38);
}
.alw-meter-label {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: rgb(75 85 99);
  white-space: nowrap;
}
</style>
